<template>
  <model-form
    ref="modelForm"
    :model="orderItem"
    modal-type="modal"
    width="95%"
    max-content-width="950px"
  >
    <q-card
      flat
      bordered
      class="col-12 bg-grey-1"
    >
      <q-card-section>
        <div class="section-title">
          Overview
        </div>
      </q-card-section>
      <q-card-section class="row items-center q-gutter-x-sm q-mx-xs">
        <q-chip
          :label="$_.startCase(orderItem.type)"
          class="col-auto"
        />
        <model-form-input
          field="description"
          class="col"
          label="Description"
          readonly
        />
        <model-form-input
          v-if="orderItem.note"
          field="note"
          class="col"
          label="Notes"
          readonly
        />
      </q-card-section>
    </q-card>
    <div class="row q-mt-md q-gutter-md">
      <div class="col">
        <q-card
          flat
          bordered
          class="q-mb-md bg-grey-1"
        >
          <q-card-section class="row justify-between">
            <div class="section-title">
              State
            </div>
            <q-btn
              icon="edit"
              size="md"
              round
              dense
              @click="$refs.orderItemDateForm.show(orderItem)"
            >
              <q-tooltip>
                <div class="text-h6">
                  Accounting Days
                </div>
                <q-chip
                  v-for="(locked, date) in order.accountingDatesLocked"
                  :key="date"
                >
                  <q-avatar
                    :icon="locked ? 'lock' : 'lock_open'"
                    :color="locked ? 'red' : 'green'"
                  />
                  {{ IsoDate.parse(date).i18n('shortDate') }}
                </q-chip>
              </q-tooltip>
            </q-btn>
          </q-card-section>
          <q-card-section>
            <order-item-state-map
              :order-item="orderItem"
              class="col q-mb-md"
              :expanded="true"
              :expandable="false"
            />
          </q-card-section>
        </q-card>
        <q-card
          flat
          bordered
          class="q-mb-md bg-grey-1"
        >
          <q-card-section class="row justify-between">
            <div class="section-title">
              Timeline
            </div>
            <q-btn
              icon="edit"
              size="md"
              round
              dense
              @click="$refs.orderItemDateForm.show(orderItem)"
            >
              <q-tooltip>
                <div class="text-h6">
                  Accounting Days
                </div>
                <q-chip
                  v-for="(locked, date) in order.accountingDatesLocked"
                  :key="date"
                >
                  <q-avatar
                    :icon="locked ? 'lock' : 'lock_open'"
                    :color="locked ? 'red' : 'green'"
                  />
                  {{ IsoDate.parse(date).i18n('shortDate') }}
                </q-chip>
              </q-tooltip>
            </q-btn>
          </q-card-section>
          <q-card-section>
            <order-item-timeline
              :order-item="orderItem"
              class="q-mt-none"
            />
          </q-card-section>
        </q-card>
      </div>
      <div class="col">
        <q-card
          flat
          bordered
          class="q-mb-md bg-grey-1"
        >
          <q-card-section>
            <div class="section-title">
              Price Details
            </div>
          </q-card-section>
          <q-card-section>
            <order-item-price-table :order-item="orderItem" />
          </q-card-section>
        </q-card>
        <q-card
          v-if="orderItem.paidAt"
          flat
          bordered
          class="q-mb-md bg-grey-1"
        >
          <q-card-section>
            <div class="section-title">
              Payment Details
            </div>
          </q-card-section>
          <q-card-section
            v-for="paymentDate in orderItem.paymentDates"
            :key="paymentDate.toString()"
          >
            <order-item-payment-table
              :order-item="orderItem"
              :payment-date="paymentDate"
            />
          </q-card-section>
        </q-card>
        <q-card
          flat
          bordered
          class="q-mb-md bg-grey-1"
        >
          <q-card-section>
            <div class="section-title">
              Revenue Recognition
            </div>
          </q-card-section>
          <q-card-section v-if="!orderItem.placedAt">
            <q-banner
              class="bg-info"
              :class="$isColorDark(currentSite.infoColor) ? 'text-white' : 'text-black'"
            >
              No revenue recognized because order item has not been placed.
            </q-banner>
          </q-card-section>
          <q-card-section v-else-if="orderItem.voidedPriorToEffectiveDate">
            <q-banner
              class="bg-warning"
              :class="$isColorDark(currentSite.warningColor) ? 'text-white' : 'text-black'"
            >
              Voided prior to effective date. No revenue recognized.
            </q-banner>
          </q-card-section>
          <template v-else>
            <q-card-section
              v-for="effectiveDate in orderItem.effectiveDates"
              :key="effectiveDate.toString()"
            >
              <order-item-revenue-table
                :order-item="orderItem"
                :effective-date="effectiveDate"
              />
            </q-card-section>
          </template>
        </q-card>
      </div>
      <order-item-date-form
        ref="orderItemDateForm"
        :order="order"
      />
    </div>
  </model-form>
</template>

<script>

import { IsoDate } from 'src/models/Model'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import OrderItemStateMap from 'src/modules/admin/order/components/OrderItemStateMap'
import OrderItemTimeline from 'src/modules/admin/order/components/OrderItemTimeline.vue'
import OrderItemDateForm from 'src/modules/admin/order/OrderItemDateForm'
import OrderItemPaymentTable from 'src/modules/admin/order/OrderItemPaymentTable'
import OrderItemPriceTable from 'src/modules/admin/order/OrderItemPriceTable'
import OrderItemRevenueTable from 'src/modules/admin/order/OrderItemRevenueTable'

export default {
  name: 'OrderItemForm',
  components: {
    ModelForm,
    OrderItemPaymentTable,
    OrderItemPriceTable,
    OrderItemRevenueTable,
    OrderItemStateMap,
    OrderItemDateForm,
    OrderItemTimeline
  },
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  configuration: ['currentSite'],
  data () {
    return {
    }
  },
  models: {
    orderItem: {
      itemOrderItem: {
        itemVariation: true,
        itemModifiers: true,
        item: true
      },
      ticket: {
        showing: {
          movie: 'default',
          screen: true
        },
        ticketType: true,
        user: 'default'
      },
      voucherPurchase: {
        voucherType: true,
        card: 'default'
      },
      tax: true,
      discount: true,
      voucher: 'default',
      voucherType: true,
      giftCard: true,

      order: 'default',
      canVoid: 'default',
      cannotVoidReason: 'default',
      collectedUser: {
        fields: ['id', 'firstName', 'lastName']
      },
      orderItemBookingFee: true
    }
  },
  created () {
    _.extend(this, {
      IsoDate
    })
  },
  methods: {
  }
}

</script>

<style lang="scss" scoped>
.section-title {
  font-size: 20px;
  letter-spacing: 0.005em;
  font-weight: 400;
  margin-bottom: -20px;
}
</style>
